// src/features/locations/IntelTab.jsx
//
// LOCATION-INTEL-TAB-S1 — Intel tab on LocationDetail.
//
// A reverse-chronological, sourced note log — a provenance/note-taking layer
// only. It NEVER feeds or changes the location's score. Notes can optionally
// be tagged and "linked" to a field name for readability, but no scoring code
// reads this table.
//
// viewer role: read-only feed. editor/admin: can add / edit / delete notes,
// each of which writes an audit_log row (handled server-side).
//
// All colors via T design tokens (INTEL_TAG_COLORS for tag dots, provenance
// badges); no raw hex in this file.

import React, { useEffect, useMemo, useState } from "react";
import { T, INTEL_TAG_COLORS, INTEL_TAG_LABELS, INTEL_SOURCE_KIND_LABELS } from "../../app/tokens.js";
import { S } from "../../app/styles.js";
import { PaneHeading } from "../../components/PaneHeading.jsx";
import { ErrorBanner } from "../../components/ErrorBanner.jsx";
import {
  getIntelNotes,
  addIntelNote,
  updateIntelNote,
  deleteIntelNote,
  getDeletedIntelNotes,
  restoreIntelNote,
} from "../../data/intelNotesClient.js";
import { log } from "../../utils/logger.js";

const TAG_ORDER = ["seats", "lease", "headcount", "cost", "utilization", "access", "market", "experience"];
const SOURCE_KIND_ORDER = ["site_visit", "broker_call", "email", "slack", "it_ticket", "facilities", "security", "other"];

function formatWhen(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

// ─── Tag pill ─────────────────────────────────────────────────────────────────

function TagPill({ tag }) {
  const color = INTEL_TAG_COLORS[tag] || T.light;
  const label = INTEL_TAG_LABELS[tag] || tag;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        fontSize: T.fsXs,
        fontWeight: T.fwSemibold,
        color: T.dark,
        background: T.lightGray,
        borderRadius: 6,
        padding: "2px 10px",
      }}
    >
      <span style={{ width: 7, height: 7, borderRadius: "50%", background: color, flexShrink: 0 }} />
      {label}
    </span>
  );
}

// ─── Intel note card ──────────────────────────────────────────────────────────

function isEdited(note) {
  if (!note.createdAt || !note.updatedAt) return false;
  const created = new Date(note.createdAt).getTime();
  const updated = new Date(note.updatedAt).getTime();
  return Number.isFinite(created) && Number.isFinite(updated) && updated > created;
}

// TASK-4065: "who" alongside "when" — sourced from the audit_log row the
// server correlates onto each note (lastActionByName/Email/At). Falls back
// gracefully when the actor can't be resolved (e.g. a system/trigger write).
function actorLabel(note) {
  return note.lastActionByName || note.lastActionByEmail || "Unknown user";
}

function editedTooltip(note) {
  const when = formatWhen(note.lastActionAt || note.updatedAt);
  return `Edited by ${actorLabel(note)}${when ? ` on ${when}` : ""}`;
}

function deletedByLine(note) {
  const when = formatWhen(note.lastActionAt || note.deletedAt);
  return `deleted by ${actorLabel(note)}${when ? ` on ${when}` : ""}`;
}

// ─── Provenance badge helpers (display-only, derived from sourceKind) ─────────
// Map sourceKind to the S/M/P/C badge shown in the "why log" panel and on each
// card. Confidence is a heuristic (not persisted; distinct from signal.confidence).
const PROVENANCE_BADGE_MAP = {
  spacebase:  { letter: "S", bg: "blueSoft",       color: "spacebaseBlue" },
  pdf:        { letter: "P", bg: "provenancePdfBg", color: "purple"       },
  computed:   { letter: "C", bg: "warmBeige",       color: "mid"          },
};
const PROVENANCE_BADGE_DEFAULT = { letter: "M", bg: "warmBeige", color: "mid" };

function provenanceBadge(sourceKind) {
  const spec = PROVENANCE_BADGE_MAP[sourceKind] || PROVENANCE_BADGE_DEFAULT;
  return { letter: spec.letter, bg: T[spec.bg], color: T[spec.color] };
}

const CONFIDENCE_MAP = {
  site_visit: "High", broker_call: "High", spacebase: "High",
  email: "Med", slack: "Med", facilities: "Med", security: "Med", pdf: "Med",
  it_ticket: "Low", other: "Low", computed: "Low",
};
function confidenceFor(sourceKind) {
  return CONFIDENCE_MAP[sourceKind] || "Low";
}
function confidenceColor(label) {
  if (label === "High") return T.utilGreenText;
  if (label === "Med")  return T.amberDark;
  return T.mid;
}

function IntelNoteCard({ note, canWrite, onDelete, onEdit }) {
  const sourceLabel = INTEL_SOURCE_KIND_LABELS[note.sourceKind] || note.sourceKind;
  const srcText = note.sourceLabel ? `${sourceLabel} · ${note.sourceLabel}` : sourceLabel;
  const edited = isEdited(note);

  return (
    <div
      data-testid="intel-note-card"
      style={{
        background: T.card,
        border: `1px solid ${T.border}`,
        borderRadius: 10,
        padding: "13px 15px",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 7 }}>
        <TagPill tag={note.tag} />
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: T.fsXs, color: T.light }}>{formatWhen(note.createdAt)}</span>
          {edited && (
            <span
              data-testid="intel-note-edited-badge"
              title={editedTooltip(note)}
              style={{ fontSize: T.fsXs, color: T.light, fontStyle: "italic" }}
            >
              edited
            </span>
          )}
          {canWrite && (
            <button
              type="button"
              data-testid="intel-note-edit-btn"
              onClick={() => onEdit(note)}
              aria-label="Edit note"
              style={{
                background: "transparent",
                border: "none",
                color: T.light,
                fontSize: T.fsXs,
                cursor: "pointer",
                padding: 2,
              }}
            >
              ✎
            </button>
          )}
          {canWrite && (
            <button
              type="button"
              data-testid="intel-note-delete-btn"
              onClick={() => onDelete(note)}
              aria-label="Delete note"
              style={{
                background: "transparent",
                border: "none",
                color: T.light,
                fontSize: T.fsXs,
                cursor: "pointer",
                padding: 2,
              }}
            >
              ✕
            </button>
          )}
        </div>
      </div>

      <div style={{ fontSize: T.fsBase5, color: T.dark, lineHeight: 1.5, marginBottom: 9 }}>
        {note.note}
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", fontSize: T.fsXs5, color: T.mid }}>
        <span
          style={{
            fontWeight: T.fwSemibold,
            color: T.mid,
            background: T.lightGray,
            border: `1px solid ${T.border}`,
            borderRadius: 6,
            padding: "2px 8px",
          }}
        >
          {srcText}
        </span>
        {note.linkedField && (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              fontWeight: T.fwSemibold,
              color: T.blue,
              background: T.blueSoft,
              border: `1px solid ${T.blueSoftBorder}`,
              borderRadius: 6,
              padding: "2px 8px",
            }}
          >
            ↳ {note.linkedField}
          </span>
        )}
        {note.attachmentUrl && (
          <a
            href={note.attachmentUrl}
            target="_blank"
            rel="noreferrer"
            style={{ display: "inline-flex", alignItems: "center", gap: 4, color: T.blue, textDecoration: "none" }}
          >
            📎 attachment
          </a>
        )}
        {/* provenance badge — derived from sourceKind, display-only */}
        {(() => {
          const badge = provenanceBadge(note.sourceKind);
          const conf  = confidenceFor(note.sourceKind);
          return (
            <>
              <span
                data-testid="intel-note-provenance-badge"
                title={`Provenance: ${badge.letter}`}
                style={{
                  width: 16, height: 16, borderRadius: "50%",
                  fontSize: T.fsXxxs, fontWeight: T.fwBold,
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                  background: badge.bg, color: badge.color,
                  fontFamily: "ui-monospace,Menlo,monospace",
                  border: `1px solid ${T.border}`,
                  flexShrink: 0,
                }}
              >
                {badge.letter}
              </span>
              <span
                data-testid="intel-note-confidence"
                style={{ fontSize: T.fsXs, color: T.mid }}
              >
                Confidence:{" "}
                <span style={{ fontWeight: T.fwSemibold, color: confidenceColor(conf) }}>
                  {conf}
                </span>
              </span>
            </>
          );
        })()}
      </div>
    </div>
  );
}

// ─── Composer ─────────────────────────────────────────────────────────────────

function Composer({ locationText, onSaved }) {
  const [note, setNote] = useState("");
  const [tag, setTag] = useState("seats");
  const [sourceKind, setSourceKind] = useState("site_visit");
  const [sourceLabel, setSourceLabel] = useState("");
  const [linkedField, setLinkedField] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  async function handleSave() {
    if (!note.trim() || saving) return;
    setSaving(true);
    setError(null);
    try {
      const created = await onSaved({
        note: note.trim(),
        tag,
        sourceKind,
        sourceLabel: sourceLabel.trim() || null,
        linkedField: linkedField.trim() || null,
      });
      if (created) {
        setNote("");
        setSourceLabel("");
        setLinkedField("");
      }
    } catch (err) {
      log.error("IntelTab: save note failed", { err: String(err) });
      setError(err.message || "Could not save note.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      data-testid="intel-composer"
      style={{
        background: T.card,
        border: `1px solid ${T.border}`,
        borderRadius: 10,
        padding: "12px 14px",
        marginBottom: 18,
      }}
    >
      {error && (
        <div style={{ marginBottom: 8 }}>
          <ErrorBanner error={error} />
        </div>
      )}
      <input
        data-testid="intel-note-input"
        placeholder={`Add intel - what did we learn about ${locationText}?`}
        value={note}
        onChange={(e) => setNote(e.target.value)}
        style={{
          width: "100%",
          border: "none",
          outline: "none",
          fontSize: T.fsBase5,
          color: T.dark,
          marginBottom: 10,
        }}
      />
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <select
            data-testid="intel-tag-select"
            value={tag}
            onChange={(e) => setTag(e.target.value)}
            style={chipSelectStyle}
          >
            {TAG_ORDER.map((t) => (
              <option key={t} value={t}>{INTEL_TAG_LABELS[t]}</option>
            ))}
          </select>
          <select
            data-testid="intel-source-select"
            value={sourceKind}
            onChange={(e) => setSourceKind(e.target.value)}
            style={chipSelectStyle}
          >
            {SOURCE_KIND_ORDER.map((k) => (
              <option key={k} value={k}>{INTEL_SOURCE_KIND_LABELS[k]}</option>
            ))}
          </select>
          <input
            data-testid="intel-source-label-input"
            placeholder="Source detail (optional)"
            value={sourceLabel}
            onChange={(e) => setSourceLabel(e.target.value)}
            style={chipInputStyle}
          />
          <input
            data-testid="intel-linked-field-input"
            placeholder="Link to field (optional)"
            value={linkedField}
            onChange={(e) => setLinkedField(e.target.value)}
            style={chipInputStyle}
          />
        </div>
        <button
          type="button"
          data-testid="intel-save-btn"
          onClick={handleSave}
          disabled={!note.trim() || saving}
          style={{
            height: 28,
            padding: "0 12px",
            borderRadius: 7,
            background: T.dark,
            color: T.white,
            border: "none",
            fontSize: T.fsXs5,
            fontWeight: T.fwSemibold,
            cursor: !note.trim() || saving ? "default" : "pointer",
            opacity: !note.trim() || saving ? 0.6 : 1,
          }}
        >
          {saving ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
}

// ─── Edit form ────────────────────────────────────────────────────────────────

function EditForm({ note, onSaved, onCancel }) {
  const [text, setText] = useState(note.note);
  const [tag, setTag] = useState(note.tag);
  const [sourceKind, setSourceKind] = useState(note.sourceKind);
  const [sourceLabel, setSourceLabel] = useState(note.sourceLabel || "");
  const [linkedField, setLinkedField] = useState(note.linkedField || "");
  const [attachmentUrl, setAttachmentUrl] = useState(note.attachmentUrl || "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  async function handleSave() {
    if (!text.trim() || saving) return;
    setSaving(true);
    setError(null);
    try {
      await onSaved({
        note: text.trim(),
        tag,
        sourceKind,
        sourceLabel: sourceLabel.trim() || null,
        linkedField: linkedField.trim() || null,
        attachmentUrl: attachmentUrl.trim() || null,
      });
    } catch (err) {
      log.error("IntelTab: update note failed", { err: String(err) });
      setError(err.message || "Could not save changes.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      data-testid="intel-edit-form"
      style={{
        background: T.card,
        border: `1px solid ${T.border}`,
        borderRadius: 10,
        padding: "13px 15px",
      }}
    >
      {error && (
        <div style={{ marginBottom: 8 }}>
          <ErrorBanner error={error} />
        </div>
      )}
      <textarea
        data-testid="intel-edit-note-input"
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={2}
        style={{
          width: "100%",
          border: `1px solid ${T.border}`,
          borderRadius: 7,
          outline: "none",
          fontSize: T.fsBase5,
          color: T.dark,
          marginBottom: 10,
          padding: "6px 8px",
          resize: "vertical",
          fontFamily: "inherit",
        }}
      />
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
        <select
          data-testid="intel-edit-tag-select"
          value={tag}
          onChange={(e) => setTag(e.target.value)}
          style={chipSelectStyle}
        >
          {TAG_ORDER.map((t) => (
            <option key={t} value={t}>{INTEL_TAG_LABELS[t]}</option>
          ))}
        </select>
        <select
          data-testid="intel-edit-source-select"
          value={sourceKind}
          onChange={(e) => setSourceKind(e.target.value)}
          style={chipSelectStyle}
        >
          {SOURCE_KIND_ORDER.map((k) => (
            <option key={k} value={k}>{INTEL_SOURCE_KIND_LABELS[k]}</option>
          ))}
        </select>
        <input
          data-testid="intel-edit-source-label-input"
          placeholder="Source detail (optional)"
          value={sourceLabel}
          onChange={(e) => setSourceLabel(e.target.value)}
          style={chipInputStyle}
        />
        <input
          data-testid="intel-edit-linked-field-input"
          placeholder="Link to field (optional)"
          value={linkedField}
          onChange={(e) => setLinkedField(e.target.value)}
          style={chipInputStyle}
        />
        <input
          data-testid="intel-edit-attachment-input"
          placeholder="Attachment URL (optional)"
          value={attachmentUrl}
          onChange={(e) => setAttachmentUrl(e.target.value)}
          style={chipInputStyle}
        />
      </div>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <button
          type="button"
          data-testid="intel-edit-cancel-btn"
          onClick={onCancel}
          disabled={saving}
          style={{
            height: 28,
            padding: "0 12px",
            borderRadius: 7,
            background: "transparent",
            color: T.mid,
            border: `1px solid ${T.border}`,
            fontSize: T.fsXs5,
            fontWeight: T.fwSemibold,
            cursor: saving ? "default" : "pointer",
          }}
        >
          Cancel
        </button>
        <button
          type="button"
          data-testid="intel-edit-save-btn"
          onClick={handleSave}
          disabled={!text.trim() || saving}
          style={{
            height: 28,
            padding: "0 12px",
            borderRadius: 7,
            background: T.dark,
            color: T.white,
            border: "none",
            fontSize: T.fsXs5,
            fontWeight: T.fwSemibold,
            cursor: !text.trim() || saving ? "default" : "pointer",
            opacity: !text.trim() || saving ? 0.6 : 1,
          }}
        >
          {saving ? "Saving…" : "Save changes"}
        </button>
      </div>
    </div>
  );
}

// ─── Deleted note card (restore view) ──────────────────────────────────────────

function DeletedNoteCard({ note, onRestore }) {
  const sourceLabel = INTEL_SOURCE_KIND_LABELS[note.sourceKind] || note.sourceKind;
  const srcText = note.sourceLabel ? `${sourceLabel} · ${note.sourceLabel}` : sourceLabel;

  return (
    <div
      data-testid="intel-deleted-note-card"
      style={{
        background: T.lightGray,
        border: `1px dashed ${T.border}`,
        borderRadius: 10,
        padding: "13px 15px",
        opacity: 0.85,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 7 }}>
        <TagPill tag={note.tag} />
        <button
          type="button"
          data-testid="intel-note-restore-btn"
          onClick={() => onRestore(note)}
          style={{
            height: 24,
            padding: "0 10px",
            borderRadius: 6,
            background: T.card,
            color: T.dark,
            border: `1px solid ${T.border}`,
            fontSize: T.fsXs,
            fontWeight: T.fwSemibold,
            cursor: "pointer",
          }}
        >
          Restore
        </button>
      </div>
      <div style={{ fontSize: T.fsBase5, color: T.dark, lineHeight: 1.5, marginBottom: 7 }}>
        {note.note}
      </div>
      <div style={{ fontSize: T.fsXs5, color: T.mid }} title={deletedByLine(note)}>
        {srcText} · {deletedByLine(note)}
      </div>
    </div>
  );
}

const chipSelectStyle = {
  fontSize: T.fsXs5,
  color: T.mid,
  background: T.lightGray,
  border: `1px solid ${T.border}`,
  borderRadius: 7,
  padding: "4px 9px",
  cursor: "pointer",
};
const chipInputStyle = {
  fontSize: T.fsXs5,
  color: T.dark,
  background: T.card,
  border: `1px solid ${T.border}`,
  borderRadius: 7,
  padding: "4px 9px",
  minWidth: 120,
};

// ─── Main component ───────────────────────────────────────────────────────────

/**
 * Intel tab content — a reverse-chronological, sourced note log for a
 * location. Provenance/note-taking layer only; never feeds scoring.
 *
 * @param {string} locationId    canonical location ID
 * @param {string} locationText  human-readable location label for the composer placeholder
 * @param {string} currentRole   "viewer" | "editor" | "admin"
 */
export function IntelTab({ locationId, locationText = "this location", currentRole }) {
  const [notes,   setNotes]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [filter,  setFilter]  = useState("all");
  const [editingId, setEditingId] = useState(null);

  const [showDeleted,    setShowDeleted]    = useState(false);
  const [deletedNotes,   setDeletedNotes]   = useState([]);
  const [deletedLoading, setDeletedLoading] = useState(false);
  const [deletedError,   setDeletedError]   = useState(null);

  const canWrite = currentRole === "editor" || currentRole === "admin";

  useEffect(() => {
    if (!locationId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    getIntelNotes(locationId)
      .then((list) => { if (!cancelled) setNotes(list); })
      .catch((err) => {
        if (cancelled) return;
        log.error("IntelTab: fetch failed", { err: String(err) });
        setError(err.message || "Could not load intel notes.");
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [locationId]);

  useEffect(() => {
    if (!canWrite || !showDeleted || !locationId) return;
    let cancelled = false;
    setDeletedLoading(true);
    setDeletedError(null);
    getDeletedIntelNotes(locationId)
      .then((list) => { if (!cancelled) setDeletedNotes(list); })
      .catch((err) => {
        if (cancelled) return;
        log.error("IntelTab: fetch deleted failed", { err: String(err) });
        setDeletedError(err.message || "Could not load deleted notes.");
      })
      .finally(() => { if (!cancelled) setDeletedLoading(false); });
    return () => { cancelled = true; };
  }, [locationId, canWrite, showDeleted]);

  const filtered = useMemo(
    () => (filter === "all" ? notes : notes.filter((n) => n.tag === filter)),
    [notes, filter]
  );

  async function handleAdd(payload) {
    const created = await addIntelNote(locationId, payload);
    setNotes((prev) => [created, ...prev]);
    return created;
  }

  async function handleUpdate(note, patch) {
    const updated = await updateIntelNote(locationId, note.id, patch);
    setNotes((cur) => cur.map((n) => (n.id === updated.id ? updated : n)));
    setEditingId(null);
    return updated;
  }

  async function handleDelete(note) {
    const prev = notes;
    setNotes((cur) => cur.filter((n) => n.id !== note.id));
    try {
      await deleteIntelNote(locationId, note.id);
    } catch (err) {
      log.error("IntelTab: delete failed", { err: String(err) });
      setNotes(prev);
      setError(err.message || "Could not delete note.");
    }
  }

  async function handleRestore(note) {
    const prevDeleted = deletedNotes;
    setDeletedNotes((cur) => cur.filter((n) => n.id !== note.id));
    try {
      const restored = await restoreIntelNote(locationId, note.id);
      setNotes((cur) => [restored, ...cur].sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      ));
    } catch (err) {
      log.error("IntelTab: restore failed", { err: String(err) });
      setDeletedNotes(prevDeleted);
      setDeletedError(err.message || "Could not restore note.");
    }
  }

  // Compute per-tag counts for the filter chips.
  const tagCounts = useMemo(() => {
    const counts = {};
    for (const note of notes) {
      counts[note.tag] = (counts[note.tag] || 0) + 1;
    }
    return counts;
  }, [notes]);

  return (
    <div data-testid="intel-tab" style={{ display: "flex", flexDirection: "column", gap: S.paneGap }}>
      <div>
        <PaneHeading style={{ marginBottom: 4 }}>Intel</PaneHeading>
      </div>

      {/* ── v5 head row: composer (left) + why-log panel (right) ─────────── */}
      {canWrite ? (
        <div style={{
          display:             "grid",
          gridTemplateColumns: "1.5fr 1fr",
          gap:                 14,
          alignItems:          "start",
        }}>
          <Composer locationText={locationText} onSaved={handleAdd} />
          <div style={{
            background:   T.blueSoft,
            border:       `1px solid ${T.border}`,
            borderRadius: 12,
            padding:      "15px 16px",
          }}>
            <div style={{ fontSize: T.fsSm, fontWeight: T.fwBold, color: T.dark, marginBottom: 6 }}>
              Why log intel here
            </div>
            <div style={{ fontSize: T.fsXs, color: T.mid, lineHeight: 1.55, marginBottom: 8 }}>
              Tagged notes become the provenance trail - trace any number back to its source,
              and feed the future AI-triage / Signals layer without changing the score.
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 4 }}>
              {[
                { label: "S", text: "Spacebase",    bg: T.blueSoft,              color: T.spacebaseBlue },
                { label: "M", text: "Manual",       bg: T.provenancePdfBg || T.warmBeige, color: T.mid },
                { label: "P", text: "PDF extract",  bg: T.purpleBg,              color: T.purple },
                { label: "C", text: "Computed",     bg: T.warmBeige,             color: T.mid },
              ].map(({ label, text, bg, color }) => (
                <span key={label} style={{ fontSize: T.fsXxs, fontWeight: T.fwSemibold, color: T.mid, display: "inline-flex", alignItems: "center", gap: 4 }}>
                  <span style={{
                    width: 15, height: 15, borderRadius: "50%", fontSize: T.fsXxxs,
                    fontWeight: T.fwBold, display: "inline-flex", alignItems: "center",
                    justifyContent: "center", background: bg, color,
                    fontFamily: "ui-monospace,Menlo,monospace",
                  }}>
                    {label}
                  </span>
                  {text}
                </span>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div style={{ fontSize: T.fsSm, color: T.mid, lineHeight: 1.5, maxWidth: 560 }}>
          A running log of what we&apos;ve learned about this location and where it came from - 
          so we can trace how a number or decision was reached later.
        </div>
      )}

      {error && <ErrorBanner error={error} />}

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {["all", ...TAG_ORDER].map((t) => {
            const active = filter === t;
            const count  = t === "all" ? notes.length : (tagCounts[t] || 0);
            return (
              <span
                key={t}
                role="button"
                tabIndex={0}
                data-testid={`intel-filter-${t}`}
                onClick={() => setFilter(t)}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setFilter(t); }}
                style={{
                  fontSize: T.fsXs,
                  color: active ? T.white : T.mid,
                  background: active ? T.dark : T.card,
                  border: `1px solid ${active ? T.dark : T.border}`,
                  borderRadius: 99,
                  padding: "4px 10px",
                  cursor: "pointer",
                }}
              >
                {t === "all" ? "All" : INTEL_TAG_LABELS[t]}
                {count > 0 && (
                  <span style={{ opacity: 0.65, fontWeight: T.fwBold, marginLeft: 4 }}>
                    {count}
                  </span>
                )}
              </span>
            );
          })}
        </div>

        {canWrite && (
          <span
            role="button"
            tabIndex={0}
            data-testid="intel-show-deleted-toggle"
            onClick={() => setShowDeleted((v) => !v)}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setShowDeleted((v) => !v); }}
            style={{
              fontSize: T.fsXs,
              color: showDeleted ? T.white : T.mid,
              background: showDeleted ? T.dark : T.card,
              border: `1px solid ${showDeleted ? T.dark : T.border}`,
              borderRadius: 99,
              padding: "4px 10px",
              cursor: "pointer",
            }}
          >
            {showDeleted ? "Hide deleted" : "Show deleted"}
          </span>
        )}
      </div>

      {loading && (
        <div style={{ padding: "24px 16px", textAlign: "center", fontSize: T.fsSm, color: T.mid }}>
          Loading intel…
        </div>
      )}

      {!loading && !error && filtered.length === 0 && (
        <div
          data-testid="intel-empty-state"
          style={{ padding: "24px 16px", textAlign: "center", fontSize: T.fsSm, color: T.mid }}
        >
          No intel logged yet.
        </div>
      )}

      {!loading && filtered.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {filtered.map((note) =>
            editingId === note.id ? (
              <EditForm
                key={note.id}
                note={note}
                onSaved={(patch) => handleUpdate(note, patch)}
                onCancel={() => setEditingId(null)}
              />
            ) : (
              <IntelNoteCard
                key={note.id}
                note={note}
                canWrite={canWrite}
                onDelete={handleDelete}
                onEdit={(n) => setEditingId(n.id)}
              />
            )
          )}
        </div>
      )}

      {canWrite && showDeleted && (
        <div data-testid="intel-deleted-section" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <div style={{ fontSize: T.fsXs5, fontWeight: T.fwSemibold, color: T.mid }}>Deleted notes</div>
          {deletedError && <ErrorBanner error={deletedError} />}
          {deletedLoading && (
            <div style={{ padding: "16px", textAlign: "center", fontSize: T.fsSm, color: T.mid }}>
              Loading deleted notes…
            </div>
          )}
          {!deletedLoading && !deletedError && deletedNotes.length === 0 && (
            <div
              data-testid="intel-deleted-empty-state"
              style={{ padding: "16px", textAlign: "center", fontSize: T.fsSm, color: T.mid }}
            >
              No deleted notes.
            </div>
          )}
          {!deletedLoading && deletedNotes.length > 0 && deletedNotes.map((note) => (
            <DeletedNoteCard key={note.id} note={note} onRestore={handleRestore} />
          ))}
        </div>
      )}

      <div style={{ fontSize: T.fsXs5, color: T.light, lineHeight: 1.5, borderTop: `1px solid ${T.border}`, paddingTop: 12 }}>
        <span style={{ fontWeight: T.fwSemibold, color: T.mid }}>Leverageable later:</span>{" "}
        notes tagged and optionally linked to a field become the provenance trail - trace any number
        back to its source, and feed the future AI-triage / Signals layer without changing the score.
      </div>
    </div>
  );
}
